/**
 * <ActivityStartCountdown> — l'entrée d'une activité, avant que le chrono parte (molecule).
 *
 * L'ancien `TimerActivityStep` posait, entre l'en-tête et l'énoncé, 2 s d'annonce (« Ton activité va
 * commencer... », ou « … reprendre... » pour une activité déjà commencée), puis, pour une activité
 * neuve, l'animation du décompte, 3, 2, 1 et le départ ; « Passer l'animation » au bas, à tout moment.
 * La même chose ici (`startCountdown.ts` tient le déroulé). Les chiffres sont la copie au design system
 * de l'animation de l'ancien (`startCountdownAnimation.ts`) : la police des titres, une face pleine sans
 * écho, le rythme des jetons, les couleurs du thème — clair ou sombre. Elle part avec le 3, calée sur le
 * son de l'ancien (un bip par chiffre). Le départ, à 3 s avec le son du départ, est un texte : « C'est
 * parti ! » arrive de la droite comme les chiffres.
 *
 * La taille se tire de la largeur de la zone du décompte, mesurée, pas de la fenêtre (`countdownFit.ts`) :
 * dans le volet de l'activité du live au grand écran, « C'est parti ! » tient sur une ligne comme en pleine
 * page (sous le plancher lisible, le titre, deux lignes) ; les chiffres prennent les trois quarts de la zone,
 * jusqu'à la taille pleine du dessin.
 *
 * En mouvement réduit, pas d'animation : chaque chiffre est un texte qui se pose sans bouger, le départ aussi
 * (`<StepStage>`) ; le déroulé, lui, reste. Le texte de chaque étape est toujours là pour un lecteur
 * d'écran, le dessin ne se lit pas.
 *
 * `onDone` part une fois : à la fin du déroulé, ou à « Passer l'animation ». Le son du départ est un
 * effet de l'écran, lancé à `onCountStart`, avec le 3 (ses bips sont calés sur le dessin). L'entrée qui
 * glisse (celle de la page d'activité qui arrive) vient ensuite : ce composant ne la remplace pas.
 *
 * `lottie-react-native` n'est lu ici que pour ce dessin, empaqueté, sans toucher : aucune de ses props ne
 * sort du composant.
 */
import LottieView from "lottie-react-native"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Animated, Easing, Platform, StyleSheet as RNStyleSheet, View } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { TextButton } from "../../atoms/Buttons/TextButton"
import { StepStage } from "../../atoms/StepStage"
import { Text } from "../../atoms/typography/Text"
import { useReduceMotion } from "../../hooks/useReduceMotion"
import { countdownFit, type CountdownFit } from "./countdownFit"
import { START_COUNTDOWN_TEXTS, startCountdownFrame, type StartCountdownKind } from "./startCountdown"
import { START_COUNTDOWN_ROLES, START_COUNTDOWN_SIZE, themedStartCountdown } from "./startCountdownAnimation"
import { useAnnounceCountdownStep } from "./announceCountdownStep"

export type ActivityStartCountdownProps = {
    /** `start` : une activité neuve, le décompte ; `resume` : une activité déjà commencée, l'annonce seule. */
    kind: StartCountdownKind
    /** Le titre de l'activité, au-dessus. Absent : rien. */
    title?: string
    /** La fin du déroulé, ou « Passer l'animation ». Une fois. */
    onDone: () => void
    /**
     * Le décompte commence (le 3, l'annonce passée) : l'écran y lance son son, calé sur le dessin. Une
     * fois, et jamais après « Passer l'animation » ni pour une reprise.
     */
    onCountStart?: () => void
    testID?: string
}

/** Le pas du déroulé : le changement de chiffre tombe à la seconde près. */
const FRAME_MS = 100

export function ActivityStartCountdown(props: ActivityStartCountdownProps) {
    const { kind, title, onDone, onCountStart, testID } = props
    const reduceMotion = useReduceMotion()
    const startedAt = useRef(Date.now())
    const [elapsed, setElapsed] = useState(0)
    const doneRef = useRef(onDone)
    doneRef.current = onDone
    const told = useRef(false)
    const finish = () => {
        if (told.current) return
        told.current = true
        doneRef.current()
    }

    const frame = startCountdownFrame(kind, elapsed)
    useAnnounceCountdownStep(frame.label)
    useEffect(() => {
        const timer = setInterval(() => setElapsed(Date.now() - startedAt.current), FRAME_MS)
        return () => clearInterval(timer)
    }, [])
    useEffect(() => {
        if (frame.done) finish()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [frame.done])

    const counting = frame.step !== "announce" && frame.step !== "done"
    const countStartRef = useRef(onCountStart)
    countStartRef.current = onCountStart
    const countStarted = useRef(false)
    useEffect(() => {
        if (!counting || countStarted.current || told.current) return
        countStarted.current = true
        countStartRef.current?.()
    }, [counting])
    const digit = frame.step === "3" || frame.step === "2" || frame.step === "1"
    // Avant la première mesure (la première image), la taille au plus grand.
    const [stageWidth, setStageWidth] = useState<number | null>(null)
    const fit = countdownFit(stageWidth ?? START_COUNTDOWN_SIZE * 2)
    return (
        <View style={styles.root} testID={testID}>
            {title != null && title !== "" && (
                <Text size="text-lg" weight="semibold" color="text-muted" center>
                    {title}
                </Text>
            )}
            <View
                style={styles.stage}
                accessibilityLiveRegion="polite"
                onLayout={(event) => setStageWidth(event.nativeEvent.layout.width)}
                testID={testID ? `${testID}-stage` : undefined}
            >
                {digit && !reduceMotion ? (
                    <>
                        <CountdownDrawing size={fit.drawingSize} testID={testID ? `${testID}-drawing` : undefined} />
                        <Text style={styles.spoken}>{frame.label}</Text>
                    </>
                ) : frame.step === "go" ? (
                    <GoLabel still={reduceMotion} fit={fit} testID={testID ? `${testID}-go` : undefined} />
                ) : (
                    <StepStage stepKey={frame.step} enter="standard">
                        {frame.label != null && (
                            <Text
                                size={digit ? "text-4xl" : "text-2xl"}
                                weight="extrabold"
                                color="role-strong"
                                center
                                style={digit ? styles.digit(fit.digitSize) : undefined}
                            >
                                {frame.label}
                            </Text>
                        )}
                    </StepStage>
                )}
            </View>
            <TextButton label={START_COUNTDOWN_TEXTS.skip} size="sm" onPress={finish} />
        </View>
    )
}

/** Le dessin du décompte, colorié au thème, joué une fois dès qu'il est posé (avec le 3), à `size` de côté. */
function CountdownDrawing({ size, testID }: { size: number; testID?: string }) {
    const { colors } = useUnistyles().theme
    const face = colors[START_COUNTDOWN_ROLES.face]
    const speed = colors[START_COUNTDOWN_ROLES.speed]
    const source = useMemo(() => themedStartCountdown({ face, speed }), [face, speed])
    return (
        <View
            style={styles.drawing(size)}
            pointerEvents="none"
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
            testID={testID}
        >
            {/* Une vue tierce : Unistyles ne la patche pas, elle prend le remplissage de React Native. */}
            {/* Le JSON est fait pour son plus grand côté : Lottie le pose en entier dans la vue (`contain`). */}
            <LottieView
                source={source}
                autoPlay
                loop={false}
                style={RNStyleSheet.absoluteFill}
                webStyle={{ width: size, height: size }}
            />
        </View>
    )
}

/**
 * « C'est parti ! » : il arrive de la droite comme un chiffre du dessin (24 px, `entree`, la courbe de la
 * marque), et reste jusqu'à la page de l'activité. En mouvement réduit, il se pose sans bouger.
 */
function GoLabel({ still, fit, testID }: { still: boolean; fit: CountdownFit; testID?: string }) {
    const { motion, spacing } = useUnistyles().theme
    const arrival = useRef(new Animated.Value(still ? 1 : 0)).current
    const [x1, y1, x2, y2] = motion.easing
    useEffect(() => {
        if (still) {
            arrival.setValue(1)
            return
        }
        const enter = Animated.timing(arrival, {
            toValue: 1,
            duration: motion.entree,
            easing: Easing.bezier(x1, y1, x2, y2),
            // react-native-web n'a pas de pilote natif ; y prétendre ne fait qu'avertir.
            useNativeDriver: Platform.OS !== "web",
        })
        enter.start()
        return () => enter.stop()
    }, [still, arrival, motion.entree, x1, y1, x2, y2])
    const translateX = arrival.interpolate({ inputRange: [0, 1], outputRange: [spacing["6"], 0] })
    return (
        <Animated.View style={{ opacity: arrival, transform: [{ translateX }] }}>
            <Text weight="extrabold" color="role-strong" center style={styles.go(fit.goSize)} testID={testID}>
                {START_COUNTDOWN_TEXTS.go}
            </Text>
        </Animated.View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: {
        flex: 1,
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: theme.spacing["4"],
        paddingVertical: theme.spacing["8"],
        gap: theme.spacing["4"],
    },
    stage: { flex: 1, alignSelf: "stretch", alignItems: "center", justifyContent: "center" },
    /** Le chiffre du décompte, en mouvement réduit : la taille de celui du dessin (`countdownFit`). */
    digit: (size: number) => ({ fontSize: size, lineHeight: Math.round(size * 1.125) }),
    /** « C'est parti ! », à la taille qui tient sur une ligne dans la zone (`countdownFit`). */
    go: (size: number) => ({ fontSize: size, lineHeight: Math.round(size * 1.15) }),
    /** Le dessin, carré : deux chiffres de haut. */
    drawing: (size: number) => ({ width: size, height: size }),
    /** Ce que dit le dessin, pour un lecteur d'écran seulement. */
    spoken: {
        position: "absolute",
        width: 1,
        height: 1,
        overflow: "hidden",
        opacity: 0,
    },
}))
